// Kontakte des Leads wie Close: Name mit Position darunter, rechts die drei Symbole E-Mail, SMS, Anruf (bei mehreren
// Adressen/Nummern ein Menü). Klick auf den Namen zeigt Nummern, E-Mails (Klick = CRM-Komposer mit diesem Empfänger)
// und Kontakt-Felder.
// Bearbeiten und neu anlegen werden nach Close geschrieben (Schlüssel des Handelnden).
import { useEffect, useRef, useState } from "react";
import { leadsApi, type Kontakt, type KontaktEingabe } from "../../api";
import { useMeta } from "../../daten/meta";
import { emailVerfassen, smsVerfassen } from "../../daten/schreiben";
import { zuletztKontaktMerken } from "../../daten/zuletzt";
import { feldText, istLeer } from "../../werkzeuge/felder";
import { KopierKnopf } from "../Bausteine";
import { IntegrationLinks } from "./IntegrationLinks";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { MenueKnopf, type MenuePunkt } from "../Menue";
import { Telefonnummer, telefonText, telefonTyp, useAnrufen } from "../Telefonnummer";
import { useWorkflowMenue } from "../../workflows";
import { Abschnitt } from "./Abschnitt";
import { Griff, ORDNEN_AKTIV, useZiehen, verschoben, type ZiehZeile } from "./ziehen";
import "../../styles/linke-spalte.css";

interface Props {
  leadId: string;
  leadName?: string | null;
  kontakte: Kontakt[];
  hervorheben?: string | null;
  onKontakt: (k: Kontakt) => void;
  /** Neue Reihenfolge (alle Kontakt-IDs); der erste Kontakt ist der bevorzugte. */
  onOrdnen: (ids: string[]) => void;
}

export function KontakteKarte({ leadId, leadName, kontakte, hervorheben, onKontakt, onOrdnen }: Props) {
  const [neu, setNeu] = useState(false);
  const verschieben = (von: number, nach: number) => onOrdnen(verschoben(kontakte, von, nach).map((k) => k.id));
  const ziehen = useZiehen("kontakt", verschieben);
  return (
    <Abschnitt
      id="kontakte"
      icon="person"
      titel="Kontakte"
      zahl={kontakte.length}
      leer={kontakte.length === 0 && !neu}
      aktionenImmer
      aktionen={
        <button type="button" className="symbolknopf symbolknopf--klein" title="Kontakt anlegen" aria-label="Kontakt anlegen" onClick={() => setNeu(true)}>
          <Icon name="plus" groesse={14} />
        </button>
      }
    >
      {kontakte.length === 0 && !neu ? (
        <p className="ls-leer">
          Keine Kontakte.{" "}
          <button type="button" className="textknopf" onClick={() => setNeu(true)}>
            Kontakt anlegen
          </button>
        </p>
      ) : null}
      {kontakte.map((k, i) => (
        <KontaktEintrag
          key={k.id}
          leadId={leadId}
          leadName={leadName}
          kontakt={k}
          hervorheben={hervorheben === k.id}
          onKontakt={onKontakt}
          ordnen={ORDNEN_AKTIV && kontakte.length > 1 ? { ziehen: ziehen(i), nachOben: i > 0 ? () => verschieben(i, 0) : undefined } : undefined}
        />
      ))}
      {neu ? (
        <KontaktEditor
          leadId={leadId}
          onFertig={(k) => {
            setNeu(false);
            if (k) onKontakt(k);
          }}
        />
      ) : null}
    </Abschnitt>
  );
}

interface Ordnen {
  ziehen: ZiehZeile;
  nachOben?: () => void;
}

function KontaktEintrag({ leadId, leadName, kontakt: k, hervorheben, onKontakt, ordnen }: { leadId: string; leadName?: string | null; kontakt: Kontakt; hervorheben: boolean; onKontakt: (k: Kontakt) => void; ordnen?: Ordnen }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [aufgeklappt, setAufgeklappt] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [blinkt, setBlinkt] = useState(hervorheben);

  useEffect(() => {
    if (!hervorheben) return;
    setBlinkt(true);
    ref.current?.scrollIntoView({ block: "center" });
    const t = window.setTimeout(() => setBlinkt(false), 2500);
    return () => window.clearTimeout(t);
  }, [hervorheben]);

  const name = k.name || k.display_name || "Ohne Namen";
  // Menü „…“ am Kontakt wie bei Close: bearbeiten und die Punkte der Workflows (Runde 4, WF-07)
  const workflows = useWorkflowMenue({ art: "kontakt", leadId, leadName: leadName || "diesem Lead", contactId: k.id, contactName: name });
  const menuePunkte: MenuePunkt[] = [
    { label: "Kontakt bearbeiten", icon: "stift", onClick: () => setBearbeiten(true) },
    ...(ordnen?.nachOben ? [{ label: "Nach oben (bevorzugt)", icon: "pfeil-hoch" as const, onClick: ordnen.nachOben }] : []),
    ...workflows.punkte.map((p, i) => (i === 0 ? { ...p, trenner: true } : p)),
  ];

  if (bearbeiten) return <KontaktEditor leadId={leadId} kontakt={k} onFertig={(neu) => (setBearbeiten(false), neu && onKontakt(neu))} />;

  return (
    <div ref={ref} data-ziehzeile className={`ls-kontakt${blinkt ? " ls-kontakt--hervor" : ""}${aufgeklappt ? " ls-kontakt--offen" : ""}`} {...ordnen?.ziehen.zeile}>
      <div className="ls-kontakt__kopf">
        {ordnen ? (
          <span className="ls-griff" title="Ziehen zum Ordnen, der oberste Kontakt ist bevorzugt" aria-hidden="true" {...ordnen.ziehen.griff}>
            <Griff />
          </span>
        ) : null}
        <button type="button" className="ls-kontakt__text" aria-expanded={aufgeklappt} title="Nummern, E-Mails und Felder zeigen" onClick={() => {
            if (!aufgeklappt) zuletztKontaktMerken(leadId, k.id, k.name || k.display_name || null); // „zuletzt angesehen“: der Kontakt dahinter
            setAufgeklappt((o) => !o);
          }}>
          <span className="ls-kontakt__name">{name}</span>
          {k.title ? <span className="ls-kontakt__titel">{k.title}</span> : null}
        </button>
        <KontaktSymbole leadId={leadId} leadName={leadName} kontakt={k} />
        <button type="button" className="symbolknopf symbolknopf--klein ls-kontakt__stift" title="Kontakt bearbeiten" aria-label="Kontakt bearbeiten" onClick={() => setBearbeiten(true)}>
          <Icon name="stift" groesse={13} />
        </button>
        <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein ls-kontakt__mehr" titel="Mehr zu diesem Kontakt" ausrichtung="end" breite={260} punkte={menuePunkte}>
          <Icon name="mehr" groesse={14} />
        </MenueKnopf>
      </div>
      {workflows.dialoge}
      {aufgeklappt ? <KontaktDetails leadId={leadId} leadName={leadName} kontakt={k} onKontakt={onKontakt} /> : null}
    </div>
  );
}

/** Die drei Symbole rechts im Kontakt (wie Close): E-Mail, SMS, Anruf. Mehrere Adressen/Nummern: Menü. */
function KontaktSymbole({ leadId, leadName, kontakt: k }: { leadId: string; leadName?: string | null; kontakt: Kontakt }) {
  const anrufen = useAnrufen();
  const schreiben = (an: string) => {
    if (!emailVerfassen({ leadId, contactId: k.id, an, leadName: leadName ?? null })) window.location.href = `mailto:${an}`;
  };
  const sms = () => {
    // Die Lead-Seite öffnet den SMS-Komposer mit diesem Kontakt als Empfänger. Nur wenn niemand das Ereignis annimmt,
    // bleibt der alte Weg über die Taste „t“ (Komposer ohne Kontaktwahl).
    if (!smsVerfassen({ leadId, contactId: k.id })) document.body.dispatchEvent(new KeyboardEvent("keydown", { key: "t", bubbles: true }));
  };
  const ruf = (nummer: string) => void anrufen(nummer, leadId, k.id, { leadName, kontaktName: k.name });
  const mailPunkte: MenuePunkt[] = k.emails.map((m) => ({ label: m.email, icon: "mail", onClick: () => schreiben(m.email) }));
  const telPunkte: MenuePunkt[] = k.phones.map((p) => ({ label: telefonText(p.phone, p.phone_formatted), icon: "telefon", rechts: telefonTyp(p.type) || undefined, onClick: () => ruf(p.phone) }));
  const einzel = (titel: string, icon: "mail" | "sms" | "telefon", onClick: () => void, aus: boolean) => (
    <button type="button" className="ls-kontakt__symbol" title={titel} aria-label={titel} disabled={aus} onClick={onClick}>
      <Icon name={icon} groesse={15} />
    </button>
  );
  return (
    <span className="ls-kontakt__symbole">
      {k.emails.length > 1 ? (
        <MenueKnopf knopfKlasse="ls-kontakt__symbol" titel="E-Mail schreiben" ausrichtung="end" punkte={mailPunkte}>
          <Icon name="mail" groesse={15} />
        </MenueKnopf>
      ) : (
        einzel(k.emails[0] ? `E-Mail an ${k.emails[0].email}` : "Keine E-Mail-Adresse", "mail", () => schreiben(k.emails[0]!.email), !k.emails.length)
      )}
      {einzel(k.phones.length ? "SMS schreiben" : "Keine Telefonnummer", "sms", sms, !k.phones.length)}
      {k.phones.length > 1 ? (
        <MenueKnopf knopfKlasse="ls-kontakt__symbol" titel="Anrufen" ausrichtung="end" punkte={telPunkte}>
          <Icon name="telefon" groesse={15} />
        </MenueKnopf>
      ) : (
        einzel(k.phones[0] ? `Anrufen: ${telefonText(k.phones[0].phone, k.phones[0].phone_formatted)}` : "Keine Telefonnummer", "telefon", () => ruf(k.phones[0]!.phone), !k.phones.length)
      )}
    </span>
  );
}

function KontaktDetails({ leadId, leadName, kontakt: k, onKontakt }: { leadId: string; leadName?: string | null; kontakt: Kontakt; onKontakt: (k: Kontakt) => void }) {
  const meta = useMeta();
  const meldung = useMeldung();
  const [mehr, setMehr] = useState(false);
  // Nummern und E-Mails umsortieren: sofort anzeigen, über den normalen Kontakt-Ändern-Weg speichern, bei Fehler zurück
  const speichern = (aenderung: Pick<Kontakt, "phones"> | Pick<Kontakt, "emails">) => {
    const neu = { ...k, ...aenderung };
    onKontakt(neu);
    leadsApi
      .kontaktAendern(k.id, {
        phones: neu.phones.map((p) => ({ phone: p.phone, type: p.type })),
        emails: neu.emails.map((m) => ({ email: m.email, type: m.type })),
      })
      .then((r) => onKontakt(r.contact))
      .catch((e) => {
        onKontakt(k);
        meldung.fehler(e, "Reihenfolge nicht gespeichert");
      });
  };
  const telVerschieben = (von: number, nach: number) => speichern({ phones: verschoben(k.phones, von, nach) });
  const mailVerschieben = (von: number, nach: number) => speichern({ emails: verschoben(k.emails, von, nach) });
  const telZiehen = useZiehen(`telefon-${k.id}`, telVerschieben);
  const mailZiehen = useZiehen(`mail-${k.id}`, mailVerschieben);
  const griff = (anzahl: number, ziehen: ZiehZeile) =>
    ORDNEN_AKTIV && anzahl > 1 ? (
      <span className="ls-griff" title="Ziehen zum Ordnen, die oberste ist bevorzugt" aria-hidden="true" {...ziehen.griff}>
        <Griff />
      </span>
    ) : null;
  const hoch = (i: number, label: string, nachOben: () => void) =>
    ORDNEN_AKTIV && i > 0 ? (
      <button type="button" className="symbolknopf symbolknopf--klein ls-kontakt__hoch" title={label} aria-label={label} onClick={nachOben}>
        <Icon name="pfeil-hoch" groesse={12} />
      </button>
    ) : null;
  const felder = (meta.meta?.custom_fields.contact ?? []).filter((f) => !istLeer(k[`custom.${f.id}`]));
  return (
    <div className="ls-kontakt__details">
      {k.phones.map((p, i) => {
        const z = telZiehen(i);
        return (
          <div key={`p${i}`} data-ziehzeile className="ls-kontakt__zeile ls-kontakt__zeile--telefon ls-kontakt__zeile--ordnen" {...z.zeile}>
            {griff(k.phones.length, z)}
            <Telefonnummer nummer={p.phone} formatiert={p.phone_formatted} typ={p.type} leadId={leadId} contactId={k.id} leadName={leadName} kontaktName={k.name} />
            {hoch(i, "Nach oben (bevorzugte Nummer)", () => telVerschieben(i, 0))}
          </div>
        );
      })}
      {k.emails.map((m, i) => {
        const z = mailZiehen(i);
        return (
        <div key={`m${i}`} data-ziehzeile className="ls-kontakt__zeile ls-kontakt__zeile--mail ls-kontakt__zeile--ordnen" {...z.zeile}>
          {griff(k.emails.length, z)}
          <Icon name="mail" groesse={13} />
          <a
            href={`mailto:${m.email}`}
            title={`E-Mail an ${m.email} schreiben`}
            onClick={(e) => {
              if (emailVerfassen({ leadId, contactId: k.id, an: m.email, leadName: leadName ?? null })) e.preventDefault();
            }}
          >
            {m.email}
          </a>
          <KopierKnopf text={m.email} label="E-Mail kopieren" />
          {hoch(i, "Nach oben (bevorzugte E-Mail)", () => mailVerschieben(i, 0))}
        </div>
        );
      })}
      {!k.phones.length && !k.emails.length ? <div className="ls-kontakt__zeile ls-leer">Keine Nummer, keine E-Mail.</div> : null}
      {/* Integration Links am Kontakt (Einstellungen › Links, Agent 8B); die Komponente zeigt ohne Links nichts */}
      <IntegrationLinks objekt="contact" objektId={k.id} />
      {felder.length ? (
        <>
          <button type="button" className="ls-umschalter ls-umschalter--klein" aria-expanded={mehr} onClick={() => setMehr((m) => !m)}>
            <Icon name={mehr ? "chevron-unten" : "chevron-rechts"} groesse={12} />
            Kontakt-Felder
            <span className="ls-umschalter__zahl">{felder.length}</span>
          </button>
          {mehr ? (
            <dl className="ls-felder ls-felder--lesen">
              {felder.map((f) => (
                <div key={f.id} className="ls-feld">
                  <dt>{f.name}</dt>
                  <dd>{feldText(f, k[`custom.${f.id}`], { nutzerName: meta.nutzerName, kontaktName: () => "Kontakt" })}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const TYPEN = [
  ["mobile", "Mobil"],
  ["office", "Büro"],
  ["direct", "Durchwahl"],
  ["home", "Privat"],
  ["other", "Sonstige"],
];

function KontaktEditor({ leadId, kontakt, onFertig }: { leadId: string; kontakt?: Kontakt; onFertig: (k: Kontakt | null) => void }) {
  const meldung = useMeldung();
  const [name, setName] = useState(kontakt?.name ?? "");
  const [titel, setTitel] = useState(kontakt?.title ?? "");
  const [phones, setPhones] = useState(() => (kontakt?.phones.length ? kontakt.phones.map((p) => ({ phone: p.phone_formatted || p.phone, type: p.type ?? "mobile" })) : [{ phone: "", type: "mobile" }]));
  const [emails, setEmails] = useState(() => (kontakt?.emails.length ? kontakt.emails.map((m) => ({ email: m.email, type: m.type ?? "office" })) : [{ email: "", type: "office" }]));
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    if (!name.trim()) {
      meldung.zeige("fehler", "Bitte einen Namen eingeben.");
      return;
    }
    const eingabe: KontaktEingabe = {
      name: name.trim(),
      title: titel.trim() || null,
      phones: phones.filter((p) => p.phone.trim()).map((p) => ({ phone: p.phone.trim(), type: p.type })),
      emails: emails.filter((m) => m.email.trim()).map((m) => ({ email: m.email.trim(), type: m.type })),
    };
    setLaeuft(true);
    try {
      const r = kontakt ? await leadsApi.kontaktAendern(kontakt.id, eingabe) : await leadsApi.kontaktAnlegen(leadId, eingabe);
      meldung.ok(kontakt ? "Kontakt gespeichert." : "Kontakt angelegt.");
      onFertig(r.contact);
    } catch (e) {
      meldung.fehler(e, "Kontakt nicht gespeichert");
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <form
      className="ls-kontakt ls-kontakt--editor"
      onSubmit={(e) => {
        e.preventDefault();
        void speichern();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onFertig(null);
        }
      }}
    >
      <input className="eingabe" placeholder="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} aria-label="Name" />
      <input className="eingabe" placeholder="Position (z. B. Geschäftsführer)" value={titel} onChange={(e) => setTitel(e.target.value)} aria-label="Position" />
      {phones.map((p, i) => (
        <div key={`p${i}`} className="ls-kontakt__eingabezeile">
          <input className="eingabe" placeholder="Telefon" value={p.phone} inputMode="tel" onChange={(e) => setPhones((l) => l.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} aria-label="Telefonnummer" />
          <select className="eingabe eingabe--schmal" value={p.type} onChange={(e) => setPhones((l) => l.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} aria-label="Art der Nummer">
            {TYPEN.map(([w, l]) => (
              <option key={w} value={w}>
                {l}
              </option>
            ))}
          </select>
          {ORDNEN_AKTIV && i > 0 ? (
            <button type="button" className="symbolknopf symbolknopf--klein" title="Nach oben (bevorzugt)" aria-label="Nummer nach oben" onClick={() => setPhones((l) => verschoben(l, i, 0))}>
              <Icon name="pfeil-hoch" groesse={13} />
            </button>
          ) : null}
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Nummer entfernen" onClick={() => setPhones((l) => l.filter((_, j) => j !== i))}>
            <Icon name="schliessen" groesse={13} />
          </button>
        </div>
      ))}
      <button type="button" className="textknopf" onClick={() => setPhones((l) => [...l, { phone: "", type: "office" }])}>
        + Nummer
      </button>
      {emails.map((m, i) => (
        <div key={`m${i}`} className="ls-kontakt__eingabezeile">
          <input className="eingabe" placeholder="E-Mail" type="email" value={m.email} onChange={(e) => setEmails((l) => l.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} aria-label="E-Mail" />
          {ORDNEN_AKTIV && i > 0 ? (
            <button type="button" className="symbolknopf symbolknopf--klein" title="Nach oben (bevorzugt)" aria-label="E-Mail nach oben" onClick={() => setEmails((l) => verschoben(l, i, 0))}>
              <Icon name="pfeil-hoch" groesse={13} />
            </button>
          ) : null}
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="E-Mail entfernen" onClick={() => setEmails((l) => l.filter((_, j) => j !== i))}>
            <Icon name="schliessen" groesse={13} />
          </button>
        </div>
      ))}
      <button type="button" className="textknopf" onClick={() => setEmails((l) => [...l, { email: "", type: "office" }])}>
        + E-Mail
      </button>
      <div className="ls-kontakt__fuss">
        <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => onFertig(null)}>
          Abbrechen
        </button>
        <button type="submit" className="knopf knopf--klein knopf--primaer" disabled={laeuft}>
          {laeuft ? "Speichert …" : kontakt ? "Speichern" : "Anlegen"}
        </button>
      </div>
    </form>
  );
}
